import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { AtendimentoTicket } from '../../../services/atendimentoService';

const sanitizeFileName = (value: string) => value.replace(/[<>:"/\\|?*]+/g, '_').replace(/\s+/g, '_').slice(0, 80);

const formatExportDateTime = () => new Date().toLocaleString('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export interface ExportarChatPdfParams {
  elementoMensagens: HTMLElement;
  elementoCabecalho?: HTMLElement | null;
  atendimento: AtendimentoTicket;
  contatoNome: string;
  chatBackground: string;
}

const copiarEstilosComputados = (origem: HTMLElement, destino: HTMLElement) => {
  const computed = window.getComputedStyle(origem);
  for (let i = 0; i < computed.length; i += 1) {
    const prop = computed[i];
    destino.style.setProperty(prop, computed.getPropertyValue(prop), computed.getPropertyPriority(prop));
  }
};

const clonarComEstilos = (elemento: HTMLElement): HTMLElement => {
  const clone = elemento.cloneNode(true) as HTMLElement;
  copiarEstilosComputados(elemento, clone);

  const origemNodes = elemento.querySelectorAll('*');
  const cloneNodes = clone.querySelectorAll('*');

  origemNodes.forEach((node, index) => {
    const alvo = cloneNodes[index];
    if (node instanceof HTMLElement && alvo instanceof HTMLElement) {
      copiarEstilosComputados(node, alvo);
    }
  });

  return clone;
};

const removerElementosNaoExportaveis = (root: HTMLElement) => {
  root.querySelectorAll('[data-export-ignore], .message-actions-button').forEach((el) => el.remove());
};

const inlineImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));

  await Promise.all(images.map(async (img) => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;

    try {
      const response = await fetch(src, { credentials: 'include' });
      if (!response.ok) return;

      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      img.src = dataUrl;
    } catch {
      // Mantém a imagem original se não for possível converter.
    }
  }));
};

const aguardarMidias = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));

  await Promise.all(images.map((img) => new Promise<void>((resolve) => {
    if (img.complete) {
      resolve();
      return;
    }

    const finalizar = () => resolve();
    img.addEventListener('load', finalizar, { once: true });
    img.addEventListener('error', finalizar, { once: true });
    window.setTimeout(finalizar, 4000);
  })));
};

const resetarPosicoesFixas = (root: HTMLElement) => {
  if (root.style.position === 'fixed' || root.style.position === 'sticky') {
    root.style.position = 'relative';
  }

  root.querySelectorAll('*').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.style.position === 'fixed' || node.style.position === 'sticky') {
      node.style.position = 'relative';
      node.style.top = 'auto';
      node.style.left = 'auto';
      node.style.right = 'auto';
      node.style.bottom = 'auto';
    }
  });
};

const aguardarLayout = () => new Promise<void>((resolve) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => resolve());
  });
});

const salvarCanvasEmPdf = (canvas: HTMLCanvasElement, fileName: string) => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const margin = 8;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * contentWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, imgHeight);
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight + margin;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;
  }

  pdf.save(fileName);
};

const montarContainerExportacao = ({
  elementoMensagens,
  elementoCabecalho,
  atendimento,
  contatoNome,
  chatBackground,
}: ExportarChatPdfParams) => {
  const overlay = document.createElement('div');
  overlay.setAttribute('data-chat-pdf-overlay', 'true');
  overlay.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 2147483646',
    'background: rgba(0, 0, 0, 0.45)',
    'display: flex',
    'align-items: flex-start',
    'justify-content: center',
    'overflow: auto',
    'padding: 24px 16px',
    'box-sizing: border-box',
  ].join(';');

  const status = document.createElement('div');
  status.textContent = 'Gerando PDF...';
  status.style.cssText = [
    'position: fixed',
    'top: 16px',
    'left: 50%',
    'transform: translateX(-50%)',
    'z-index: 2147483647',
    'background: #fff',
    'color: #333',
    'padding: 10px 18px',
    'border-radius: 8px',
    'font-family: Arial, sans-serif',
    'font-size: 14px',
    'font-weight: 600',
    'box-shadow: 0 8px 24px rgba(0,0,0,0.2)',
  ].join(';');

  const exportBox = document.createElement('div');
  const exportWidth = Math.max(elementoMensagens.scrollWidth, elementoMensagens.offsetWidth, 720);
  exportBox.style.cssText = [
    `width: ${exportWidth}px`,
    `background: ${chatBackground}`,
    'box-sizing: border-box',
    'box-shadow: 0 12px 40px rgba(0,0,0,0.25)',
    'border-radius: 8px',
    'overflow: visible',
  ].join(';');

  if (elementoCabecalho) {
    const headerClone = clonarComEstilos(elementoCabecalho);
    headerClone.style.width = '100%';
    headerClone.style.flexShrink = '0';
    removerElementosNaoExportaveis(headerClone);
    exportBox.appendChild(headerClone);
  } else {
    const header = document.createElement('div');
    header.style.cssText = 'padding: 12px 16px; background: #fff; border-bottom: 1px solid #e0e0e0;';
    header.innerHTML = `
      <div style="font-size: 15px; font-weight: 700; color: #1a1a1a;">#${atendimento.id} - ${contatoNome}</div>
      <div style="font-size: 11px; color: #666; margin-top: 4px;">${atendimento.contato?.numero || ''}</div>
    `;
    exportBox.appendChild(header);
  }

  const meta = document.createElement('div');
  meta.style.cssText = 'padding: 8px 16px 12px; font-size: 10px; color: #888; border-bottom: 1px solid rgba(0,0,0,0.08); background: #fff;';
  meta.textContent = `Exportado em ${formatExportDateTime()}`;
  exportBox.appendChild(meta);

  const messagesClone = clonarComEstilos(elementoMensagens);
  messagesClone.style.width = '100%';
  messagesClone.style.height = 'auto';
  messagesClone.style.maxHeight = 'none';
  messagesClone.style.overflow = 'visible';
  messagesClone.style.flex = 'none';
  messagesClone.style.padding = '16px 24px';
  messagesClone.style.boxSizing = 'border-box';
  removerElementosNaoExportaveis(messagesClone);
  exportBox.appendChild(messagesClone);

  overlay.appendChild(exportBox);
  document.body.appendChild(overlay);
  document.body.appendChild(status);

  resetarPosicoesFixas(exportBox);

  return { overlay, status, exportBox };
};

export async function exportarChatPdf(params: ExportarChatPdfParams): Promise<void> {
  const { elementoMensagens, atendimento, contatoNome } = params;

  if (!elementoMensagens.children.length) {
    throw new Error('Nenhuma mensagem para exportar.');
  }

  const { overlay, status, exportBox } = montarContainerExportacao(params);

  try {
    await aguardarLayout();
    await inlineImages(exportBox);
    await aguardarMidias(exportBox);
    await aguardarLayout();

    const canvas = await html2canvas(exportBox, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: params.chatBackground,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      width: exportBox.scrollWidth,
      height: exportBox.scrollHeight,
      windowWidth: exportBox.scrollWidth,
      windowHeight: exportBox.scrollHeight,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error('Falha ao capturar o conteúdo do chat.');
    }

    const fileName = `atendimento_${atendimento.id}_${sanitizeFileName(contatoNome)}.pdf`;
    salvarCanvasEmPdf(canvas, fileName);
  } finally {
    status.remove();
    overlay.remove();
  }
}
